import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import { PopoverContent } from './Animations';
import { cn } from './cn';

export interface NativeSelectOption {
  key: string;
  text: string;
  /** Muted context shown after the text, e.g. the owner of a personal workspace. */
  secondaryText?: string;
  disabled?: boolean;
}

export interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: NativeSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const NativeSelect = ({ value, onChange, options, placeholder, disabled, className, id }: NativeSelectProps) => {
  const [open, setOpen] = React.useState(false);
  const selectedOption = options.find((o) => o.key === value);
  const displayText = selectedOption?.text || placeholder || 'Select...';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          className={cn(
            'inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-surface pl-3 pr-2.5 text-sm',
            'transition-colors duration-200 cursor-pointer',
            'hover:border-info-300',
            open && 'border-info-500 ring-1 ring-info-500',
            disabled && 'opacity-50 cursor-not-allowed',
            !selectedOption && 'text-text-muted',
            selectedOption && 'text-text-primary',
            className
          )}
        >
          <span className="truncate">
            {displayText}
            {selectedOption?.secondaryText && (
              <span className="text-text-muted"> ({selectedOption.secondaryText})</span>
            )}
          </span>
          <ChevronDownIcon
            className={cn('h-4 w-4 shrink-0 text-text-muted transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
          sideOffset={4}
          align="start"
          asChild
          style={{ minWidth: 'var(--radix-popover-trigger-width)' }}
          // Inside a modal Sheet/Dialog the scroll lock swallows wheel/touch events on portaled
          // popovers; stopping propagation keeps them local so the list scrolls (same fix as
          // SearchCombobox).
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <PopoverContent>
            <div className="max-h-60 overflow-y-auto overscroll-contain py-1">
              {options.length === 0 && <div className="px-3 py-2 text-sm text-text-muted italic">No items</div>}
              {options.map((opt) => {
                const isSelected = opt.key === value;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={opt.disabled}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-100 cursor-pointer bg-transparent border-none',
                      isSelected
                        ? 'bg-primary-100/50 text-info-600 font-medium dark:bg-primary-500/15 dark:text-info-400'
                        : 'text-text-primary hover:bg-surface-page dark:hover:bg-white/5',
                      opt.disabled && 'opacity-40 cursor-not-allowed'
                    )}
                    onClick={() => {
                      if (!opt.disabled) {
                        onChange(opt.key);
                        setOpen(false);
                      }
                    }}
                  >
                    <span className="w-4 shrink-0">
                      {isSelected && <CheckIcon className="h-4 w-4 text-info-500" />}
                    </span>
                    <span className="truncate">
                      {opt.text}
                      {opt.secondaryText && <span className="text-text-muted"> ({opt.secondaryText})</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
